import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faHome, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Row } from "@themesberg/react-bootstrap";
import { Link, useParams, generatePath } from "react-router-dom";

import { Routes } from "../routes";
import defaultGroups from "../data/groups";
import defaultGroupTransactions from "../data/groupTransactions";
import { normalizePhone, parseMpesaMessage } from "../utils/mpesa";
import { enqueueSync } from "../utils/syncQueue";
import { apiRequest, getAuthToken } from "../utils/api";
import { formatPersonName } from "../utils/personName";
import ConfirmationToast from "../components/ConfirmationToast";

const GROUPS_STORAGE_KEY = "treasurer.groups";
const TRANSACTIONS_STORAGE_KEY = "treasurer.groupTransactions";
const PURPOSES_CACHE_PREFIX = "treasurer.contributionPurposes.";

/** YYYY-MM-DD in the user's local timezone (for date inputs). */
const getLocalDateString = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const loadCachedPurposes = (groupId) => {
  if (!groupId) return [];
  const stored = localStorage.getItem(PURPOSES_CACHE_PREFIX + groupId);
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const loadGroups = () => {
  const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
  if (!stored) {
    return defaultGroups;
  }
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return defaultGroups;
    }
    return parsed.map((group) => ({
      ...group,
      frequency: group.frequency || "monthly",
      members: Array.isArray(group.members)
        ? group.members
            .map((member) => {
              if (typeof member === "string") {
                return { id: null, name: member, phone: "", info: "" };
              }
              return {
                id: member?.id ?? null,
                name: member?.name || "",
                phone: member?.phone || "",
                info: member?.info || ""
              };
            })
            .filter((member) => member.name)
        : []
    }));
  } catch (error) {
    return defaultGroups;
  }
};

const loadTransactions = () => {
  const stored = localStorage.getItem(TRANSACTIONS_STORAGE_KEY);
  if (!stored) {
    return defaultGroupTransactions;
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : defaultGroupTransactions;
  } catch (error) {
    return defaultGroupTransactions;
  }
};

export default () => {
  const { groupId } = useParams();
  const [groups, setGroups] = useState(loadGroups);
  const [transactions, setTransactions] = useState(loadTransactions);
  const [message, setMessage] = useState("");
  const [cashForm, setCashForm] = useState(() => ({
    name: "",
    amount: "",
    date: getLocalDateString(),
    phone: ""
  }));
  const [cashMemberSelect, setCashMemberSelect] = useState(""); // "" = not selected, "__other__" = manual entry
  const [error, setError] = useState("");
  const [contributionPurposes, setContributionPurposes] = useState(() => loadCachedPurposes(groupId));
  const [selectedPurposeId, setSelectedPurposeId] = useState("");
  const [toast, setToast] = useState({ show: false, variant: "success", message: "" });

  const showToast = (variant, message) => {
    setToast({ show: true, variant, message });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  useEffect(() => {
    localStorage.setItem(TRANSACTIONS_STORAGE_KEY, JSON.stringify(transactions));
  }, [transactions]);

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  }, [groups]);

  useEffect(() => {
    setContributionPurposes(loadCachedPurposes(groupId));
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    setCashForm((prev) => ({ ...prev, date: getLocalDateString() }));
  }, [groupId]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !groupId) {
      return;
    }
    apiRequest(`/groups/${groupId}/contribution-purposes`)
      .then((data) => {
        if (data?.purposes) {
          setContributionPurposes(
            data.purposes.map((p) => ({
              id: String(p.id),
              name: p.name,
              description: p.description || ""
            }))
          );
        }
      })
      .catch(() => {});
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    localStorage.setItem(PURPOSES_CACHE_PREFIX + groupId, JSON.stringify(contributionPurposes));
  }, [groupId, contributionPurposes]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      return;
    }
    let isMounted = true;
    const loadRemote = async () => {
      try {
        const [groupData, membersData, transactionsData] = await Promise.all([
          apiRequest(`/groups/${groupId}`),
          apiRequest(`/groups/${groupId}/members`),
          apiRequest(`/groups/${groupId}/transactions`)
        ]);
        if (!isMounted) {
          return;
        }
        if (groupData?.group) {
          setGroups((prev) => {
            const next = prev.map((item) =>
              String(item.id) === String(groupId)
                ? {
                    ...item,
                    name: groupData.group.name,
                    purpose: groupData.group.purpose,
                    frequency: groupData.group.frequency || "monthly",
                    createdAt: groupData.group.createdAt || item.createdAt
                  }
                : item
            );
            if (next.some((item) => String(item.id) === String(groupId))) {
              return next;
            }
            return [
              ...next,
              {
                id: String(groupData.group.id),
                name: groupData.group.name,
                purpose: groupData.group.purpose,
                frequency: groupData.group.frequency || "monthly",
                createdAt: groupData.group.createdAt || new Date().toISOString(),
                members: []
              }
            ];
          });
        }
        if (membersData?.members) {
          setGroups((prev) =>
            prev.map((item) => {
              if (String(item.id) !== String(groupId)) {
                return item;
              }
              return {
                ...item,
                members: membersData.members.map((member) => ({
                  id: member.id,
                  name: member.name,
                  phone: member.phone || "",
                  info: member.info || ""
                }))
              };
            })
          );
        }
        if (transactionsData?.transactions) {
          const normalized = transactionsData.transactions.map((transaction) => ({
            id: String(transaction.id),
            groupId: String(groupId),
            type: transaction.type,
            message: transaction.message || "",
            sender: transaction.sender || "",
            amount: Number(transaction.amount || 0),
            date: transaction.transactionDate || "",
            purposeId: transaction.purposeId != null ? transaction.purposeId : null,
            purpose: transaction.purpose || null,
            createdAt: transaction.createdAt || new Date().toISOString()
          }));
          setTransactions((prev) => {
            const other = prev.filter((item) => String(item.groupId) !== String(groupId));
            return [...normalized, ...other];
          });
        }
      } catch (error) {
        // keep local data
      }
    };
    loadRemote();
    return () => {
      isMounted = false;
    };
  }, [groupId]);

  const group = useMemo(
    () => groups.find((item) => String(item.id) === String(groupId)),
    [groups, groupId]
  );

  const groupMembers = useMemo(() => {
    return Array.isArray(group?.members) ? group.members : [];
  }, [group]);

  const memberNameLookup = useMemo(() => {
    const map = new Map();
    groupMembers.forEach((member) => {
      if (member.name) {
        map.set(member.name.toLowerCase(), member);
      }
    });
    return map;
  }, [groupMembers]);

  const handleAddTransaction = (event) => {
    event.preventDefault();
    const parsed = parseMpesaMessage(message);
    if (!parsed.isValid) {
      setError("Please paste the full M-Pesa message so we can read the amount and sender.");
      return;
    }
    const purposeIdNum = selectedPurposeId ? Number(selectedPurposeId) : null;
    if (!purposeIdNum) {
      setError("Please select a purpose for this contribution.");
      return;
    }

    const tempId = `t-${Date.now()}`;
    const transaction = {
      id: tempId,
      groupId: String(groupId),
      groupName: group?.name || "",
      message: parsed.raw,
      code: parsed.code,
      sender: parsed.sender,
      phone: parsed.phone,
      amount: parsed.amount || 0,
      date: parsed.timestamp,
      purposeId: purposeIdNum,
      createdAt: new Date().toISOString(),
      type: "mpesa"
    };
    setTransactions((prev) => [transaction, ...prev]);
    enqueueSync({ type: "transaction.create", payload: transaction });
    const token = getAuthToken();
    if (token && navigator.onLine) {
      apiRequest(`/groups/${groupId}/transactions`, {
        method: "POST",
        body: JSON.stringify({
          type: "mpesa",
          code: parsed.code,
          sender: parsed.sender,
          amount: parsed.amount || 0,
          message: parsed.raw,
          transactionDate: parsed.timestamp,
          purposeId: purposeIdNum
        })
      })
        .then((data) => {
          if (data?.transaction?.id) {
            setTransactions((prev) =>
              prev.map((item) =>
                item.id === tempId
                  ? { ...item, id: String(data.transaction.id), purposeId: data.transaction.purposeId ?? purposeIdNum }
                  : item
              )
            );
          }
          showToast("success", "M-Pesa contribution recorded.");
        })
        .catch((err) => {
          showToast("danger", err?.message || "Failed to save. It will sync when you're back online.");
        });
    } else {
      showToast("success", "M-Pesa contribution recorded. It will sync when you're back online.");
    }
    setMessage("");
    setError("");
  };

  const handleCashChange = (event) => {
    const { name, value } = event.target;
    if (name === "date") {
      const today = getLocalDateString();
      const nextDate = value && value > today ? today : value;
      setCashForm((prev) => ({ ...prev, date: nextDate }));
      return;
    }
    setCashForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "name" ? { phone: "" } : {})
    }));
  };

  const todayLocal = getLocalDateString();
  const canSaveCash =
    cashForm.name.trim().length > 0 &&
    cashForm.amount !== "" &&
    Number(cashForm.amount) > 0 &&
    cashForm.date.trim().length > 0 &&
    cashForm.date <= todayLocal &&
    (groupMembers.length === 0 || cashMemberSelect);

  const handleAddCash = (event) => {
    event.preventDefault();
    const purposeIdNum = selectedPurposeId ? Number(selectedPurposeId) : null;
    if (!purposeIdNum) {
      setError("Please select a purpose for this contribution.");
      return;
    }
    if (cashForm.date > todayLocal) {
      setError("Contribution date cannot be in the future.");
      return;
    }
    if (!canSaveCash) {
      return;
    }
    const tempId = `c-${Date.now()}`;
    const matchedMember = memberNameLookup.get(cashForm.name.trim().toLowerCase());
    const resolvedPhone = normalizePhone(cashForm.phone || matchedMember?.phone || "");
    const transaction = {
      id: tempId,
      groupId: String(groupId),
      groupName: group?.name || "",
      type: "cash",
      sender: cashForm.name.trim(),
      phone: resolvedPhone,
      amount: Number(cashForm.amount),
      date: cashForm.date,
      purposeId: purposeIdNum,
      details: "Cash contribution",
      createdAt: new Date().toISOString()
    };
    setTransactions((prev) => [transaction, ...prev]);
    enqueueSync({ type: "transaction.create", payload: transaction });
    const token = getAuthToken();
    if (token && navigator.onLine) {
      apiRequest(`/groups/${groupId}/transactions`, {
        method: "POST",
        body: JSON.stringify({
          type: "cash",
          sender: transaction.sender,
          amount: transaction.amount,
          transactionDate: transaction.date,
          purposeId: purposeIdNum
        })
      })
        .then((data) => {
          if (data?.transaction?.id) {
            setTransactions((prev) =>
              prev.map((item) =>
                item.id === tempId
                  ? { ...item, id: String(data.transaction.id), purposeId: data.transaction.purposeId ?? purposeIdNum }
                  : item
              )
            );
          }
          showToast("success", "Cash contribution recorded.");
        })
        .catch((err) => {
          showToast("danger", err?.message || "Failed to save. It will sync when you're back online.");
        });
    } else {
      showToast("success", "Cash contribution recorded. It will sync when you're back online.");
    }
    setCashForm({ name: "", amount: "", date: getLocalDateString(), phone: "" });
    setCashMemberSelect("");
    setError("");
  };

  const ledgerPath = generatePath(Routes.GroupTransactions.path, { groupId });

  return (
    <>
      <ConfirmationToast
        show={toast.show}
        variant={toast.variant}
        message={toast.message}
        onClose={closeToast}
      />

      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={Routes.Groups.path}>Groups</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={ledgerPath}>{group?.name || "Group"}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Record income</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Record income</h4>
          <p className="mb-0">Paste M-Pesa messages or record cash received for this group.</p>
        </div>
        <Button
          as={Link}
          to={ledgerPath}
          variant="outline-primary"
          size="sm"
          className="d-inline-flex align-items-center"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to group
        </Button>
      </div>

      <Row>
        <Col xs={12} lg={8}>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Paste M-Pesa message</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleAddTransaction}>
                <Form.Group className="mb-3">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Select
                    value={selectedPurposeId}
                    onChange={(e) => setSelectedPurposeId(e.target.value)}
                    required
                  >
                    <option value="">Select purpose</option>
                    {contributionPurposes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Form.Select>
                  {contributionPurposes.length === 0 ? (
                    <p className="text-muted small mt-1 mb-0">
                      <Link to={generatePath(Routes.GroupContributionPurposes.path, { groupId })}>Add purposes for this group</Link>.
                    </p>
                  ) : null}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Message from M-Pesa</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Paste the message you received from M-Pesa here."
                  />
                  {error ? <small className="text-danger">{error}</small> : null}
                </Form.Group>
                <Button type="submit" variant="primary" disabled={!selectedPurposeId || contributionPurposes.length === 0}>
                  <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                  Add
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card border="light" className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Record cash received</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleAddCash}>
                <Form.Group className="mb-3">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Select
                    value={selectedPurposeId}
                    onChange={(e) => setSelectedPurposeId(e.target.value)}
                    required
                  >
                    <option value="">Select purpose</option>
                    {contributionPurposes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Who contributed?</Form.Label>
                  {groupMembers.length > 0 ? (
                    <>
                      <Form.Select
                        required
                        value={cashMemberSelect}
                        onChange={(e) => {
                          const v = e?.target?.value ?? "";
                          setCashMemberSelect(v);
                          if (v === "__other__") {
                            setCashForm((prev) => ({ ...prev, name: "", phone: "" }));
                            return;
                          }
                          const idx = Number(v);
                          const member = groupMembers[idx];
                          setCashForm((prev) => ({
                            ...prev,
                            name: member?.name ?? "",
                            phone: member?.phone ?? ""
                          }));
                        }}
                      >
                        <option value="">Select member</option>
                        {groupMembers.map((member, idx) => (
                          <option key={member.id ?? `${member.name}-${idx}`} value={idx}>
                            {formatPersonName(member.name) || member.name}
                          </option>
                        ))}
                        <option value="__other__">Not in list</option>
                      </Form.Select>

                      {cashMemberSelect === "__other__" ? (
                        <Form.Control
                          required
                          type="text"
                          name="name"
                          value={cashForm.name}
                          placeholder="Enter name"
                          onChange={handleCashChange}
                        />
                      ) : null}
                    </>
                  ) : (
                    <Form.Control
                      required
                      type="text"
                      name="name"
                      value={cashForm.name}
                      placeholder="e.g. Alice Kamau"
                      onChange={handleCashChange}
                    />
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Amount (Ksh)</Form.Label>
                  <Form.Control
                    required
                    type="number"
                    name="amount"
                    min="0"
                    step="0.01"
                    value={cashForm.amount}
                    onChange={handleCashChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    required
                    type="date"
                    name="date"
                    value={cashForm.date}
                    max={todayLocal}
                    onChange={handleCashChange}
                  />
                  <Form.Text className="text-muted">Uses your device&apos;s date. Future dates are not allowed.</Form.Text>
                </Form.Group>
                <Button type="submit" variant="primary" disabled={!canSaveCash || !selectedPurposeId || contributionPurposes.length === 0}>
                  Record cash
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
